import React, { useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from "react-router-dom";
import axios from 'axios';
import { pdf } from '@react-pdf/renderer';
import HotelInvoicePDF from './HotelInvoicePdf';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { storage } from '../firebaseconfig';
import { KeyUri } from '../key';
import { toast } from "react-toastify";
import {
  Check,
  Copy,
  MapPin,
  Calendar,
  Users,
  ShieldCheck,
  Download,
  Phone,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  Star,
  MessageCircle,
  X
} from 'lucide-react';

export default function HotelBookingInvoice() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const bookingIdFromState = location.state?.bookingId;
  const bookingId = searchParams.get('bookingId') || bookingIdFromState;

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(true);
  const [copied, setCopied] = useState(false);

  const [showHostModal, setShowHostModal] = useState(false);
  const [hostCopied, setHostCopied] = useState(false);

 
  const [invoiceSynced, setInvoiceSynced] = useState(false);
const [paymentFailed, setPaymentFailed] = useState(false);

   useEffect(() => {
    if (!bookingId) {
      setLoading(false);
      return;
    }

    const orderId = searchParams.get("order_id"); // present only on HDFC return

    const fetchBookingData = async () => {
      try {
        if (orderId) {
          try {
            const verifyRes = await axios.post(
              `${KeyUri.BACKEND_URI}/payments/hdfc/verify`,
              { orderId },
              { headers: { "Content-Type": "application/json" } }
            );
            if (!verifyRes.data.success) {
              setPaymentFailed(true);
              setBooking(null);
              setLoading(false);
              return;
            }
          } catch (verifyErr) {
            console.error("HDFC verify failed:", verifyErr);
            setPaymentFailed(true);
            setBooking(null);
            setLoading(false);
            return;
          }
        }

        const bookingRes = await axios.get(
          `${KeyUri.BACKEND_URI}/hotelbookings/${bookingId}`
        );
        setBooking(bookingRes.data.data || bookingRes.data);
      } catch (error) {
        console.error('Error fetching booking data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBookingData();
  }, [bookingId, searchParams]);

 
  useEffect(() => {
    if (!booking || invoiceSynced) return;

        if (["pending", "failed"].includes(booking.status)) return;

    const generateAndUpload = async () => {
      try {
        setInvoiceSynced(true);
    
        // 1) Generate PDF blob
        const blob = await pdf(
          <HotelInvoicePDF invoiceData={booking} />
        ).toBlob();
      

        // 2) Upload to Firebase
        const storageRef = ref(storage, `hotel-pdfs/hotel_${booking._id}.pdf`);
     
        await uploadBytes(storageRef, blob);
      

        // 3) Optional debug URL
        const downloadURL = await getDownloadURL(storageRef);
      

        // 4) Trigger WhatsApp confirmation AFTER upload
        try {
          await axios.post(`${KeyUri.BACKEND_URI}/hotelbookings/confirm`, {
            booking_idd: booking._id,
          });
        
        } catch (confirmErr) {
          console.error(" Auto confirmation failed (non-blocking):", confirmErr);
        }
      } catch (err) {
      
      }
    };

    generateAndUpload();
  }, [booking, invoiceSynced]);

  // Manual download button
  const handleDownloadPDF = async () => {
    if (loading || !booking) {
      toast.error('Loading data. Please wait.');
      return;
    }

    try {
      setIsGeneratingPdf(true);
     

      const blob = await pdf(
        <HotelInvoicePDF invoiceData={booking} />
      ).toBlob();
    
      const storageRef = ref(storage, `hotel-pdfs/hotel_${booking._id}.pdf`);
      await uploadBytes(storageRef, blob);
  

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `HotelBookingInvoice_${booking.bookingId || booking._id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('✅ Invoice Downloaded!');
    } catch (err) {
      console.error('❌ HOTEL PDF FAILED:', err);
      toast.error('Failed to generate/download invoice');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handleCopy = () => {
    if (!booking?._id) return;

    const textArea = document.createElement("textarea");
    textArea.value = booking._id;
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
    }
    document.body.removeChild(textArea);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 text-gray-700 text-sm">
        Loading your booking details...
      </div>
    );
  }

     if (paymentFailed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 text-gray-700 text-sm text-center px-4">
        Your payment was not completed. If money was deducted, it will be confirmed shortly. Otherwise please try booking again.
      </div>
    );
  }

    if (booking && ["pending", "failed"].includes(booking.status)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 text-gray-700 text-sm text-center px-4">
        Payment for this booking has not been completed.
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 text-gray-700 text-sm">
        No booking details found. Please check your booking ID.
      </div>
    );
  }

  const property = booking.propertyId || {};
  const acc = booking.accommodationId || {};
  const loc = property.location || {};

  const handleCopyHostNumber = async () => {
    const hostPhone = property.contact?.phone;
    if (!hostPhone) {
     // console.log('No host phone available');
      return;
    }

    try {
      await navigator.clipboard.writeText(hostPhone);
      setHostCopied(true);
      setTimeout(() => setHostCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err);
      const textArea = document.createElement("textarea");
      textArea.value = hostPhone;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setHostCopied(true);
      setTimeout(() => setHostCopied(false), 2000);
    }
  };

  const checkIn = booking.checkIn
    ? new Date(booking.checkIn).toLocaleDateString('en-IN', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    : 'N/A';

  const checkOut = booking.checkOut
    ? new Date(booking.checkOut).toLocaleDateString('en-IN', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    : 'N/A';

  const guestsDescription = `${booking.guests || 1} Guest${booking.guests > 1 ? 's' : ''}`;

  const handleAddToCalendar = () => {
    if (!booking.checkIn || !booking.checkOut) {
      alert('Missing check-in/out dates');
      return;
    }

    const checkInDate = new Date(booking.checkIn).toISOString().split('T')[0];
    const checkOutDate = new Date(booking.checkOut).toISOString().split('T')[0];

    const title = `Stay at ${property.propertyName}`;
    const locationStr = loc.address || loc.city || 'Your Hotel';
    const details = `Check-in: ${checkIn}\nCheck-out: ${checkOut}\nGuests: ${guestsDescription}\nBooking ID: ${booking._id?.slice(-8)}`;

    const googleUrl =
      `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}` +
      `&dates=${checkInDate}T14:00:00/${checkOutDate}T11:00:00&details=${encodeURIComponent(details)}` +
      `&location=${encodeURIComponent(locationStr)}`;

    const appleIcs = `BEGIN:VCALENDAR
VERSION:2.0
BEGIN:VEVENT
UID:${booking._id}@hotelbooking
DTSTART:${checkInDate.replace(/-/g, '')}T140000
DTEND:${checkOutDate.replace(/-/g, '')}T110000
SUMMARY:${title}
DESCRIPTION:${details.replace(/\n/g, '\\n')}
LOCATION:${locationStr}
END:VEVENT
END:VCALENDAR`;

    if (/iPhone|iPad|iPod/.test(navigator.userAgent)) {
      const blob = new Blob([appleIcs], { type: 'text/calendar' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title.replace(/[^a-zA-Z0-9]/g, '_')}.ics`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } else if (/Android/.test(navigator.userAgent)) {
      window.open(googleUrl, '_blank');
    } else {
      window.open(googleUrl, '_blank');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans text-gray-900 pb-12 mt-32">
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        {/* Hero */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center p-3 bg-green-100 rounded-full mb-4 ring-8 ring-green-50 relative">
            <Check size={40} className="text-green-600 animate-bounce" strokeWidth={3} />
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-2">
            Booking Confirmed!
          </h1>
          <p className="text-lg text-gray-600 max-w-xl mx-auto">
            Pack your bags, you're going to{' '}
            <span className="font-semibold text-gray-900">
              {loc.city || property.propertyName || 'Your Destination'}
            </span>.
            We've sent a confirmation email to <span className="font-semibold">{booking.email}</span>.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left: main card */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              {/* Header image */}
              <div className="relative h-48 sm:h-64 bg-gray-200">
                {property.images && property.images.length > 0 && property.images[0]?.url ? (
                  <img
                    src={property.images[0].url}
                    alt={property.propertyName || 'Property'}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-gray-200 via-indigo-200 to-purple-300 flex items-center justify-center">
                    <span className="text-5xl opacity-20">🏨</span>
                  </div>
                )}
                <div className="absolute top-4 left-4 flex gap-2">
                  {property.averageRating && property.averageRating > 0 && (
                    <span className="bg-white/95 backdrop-blur px-3 py-1 rounded-full text-xs font-bold text-gray-800 shadow-sm flex items-center gap-1">
                      <Star size={12} className="text-orange-500 fill-orange-500" />
                      {property.averageRating.toFixed(1)}
                    </span>
                  )}
                  <span className="bg-indigo-600/90 backdrop-blur px-3 py-1 rounded-full text-xs font-bold text-white shadow-sm">
                    {property.propertyType || 'Hotel'}
                  </span>
                </div>
              </div>

              {/* Body */}
              <div className="p-6">
                {/* Title + ID */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 leading-tight">
                      {property.propertyName || acc.name || 'Your Accommodation'}
                    </h2>
                    <div className="flex items-center text-gray-500 mt-1 text-sm font-medium">
                      <MapPin size={16} className="mr-1.5 text-indigo-500" />
                      <span>
                        {loc.address ? `${loc.address}, ${loc.city}` : loc.city || 'Location'}
                      </span>
                    </div>
                  </div>

                  <div className="w-full sm:w-auto bg-gray-50 border border-gray-100 rounded-lg p-3 flex flex-col items-center sm:items-end">
                    <span className="text-xs uppercase tracking-wider text-gray-400 font-bold mb-1">
                      Reference ID
                    </span>
                    <button
                      onClick={handleCopy}
                      className="group flex items-center gap-2 text-indigo-700 font-mono text-lg font-bold hover:text-indigo-900 transition-colors"
                    >
                      {booking._id?.slice(-8).toUpperCase() || 'N/A'}
                      {copied ? (
                        <Check size={18} className="text-green-500" />
                      ) : (
                        <Copy size={18} className="text-indigo-300 group-hover:text-indigo-600" />
                      )}
                    </button>
                    {copied && (
                      <span className="text-xs text-green-600 font-medium mt-1">Copied!</span>
                    )}
                  </div>
                </div>

                {/* Timeline */}
                <div className="flex flex-col sm:flex-row gap-6 mb-8 relative">
                  <div className="hidden sm:block absolute top-1/2 left-0 right-0 h-0.5 bg-gray-100 -z-10 transform -translate-y-1/2 mx-12" />

                  <div className="flex-1 bg-white sm:bg-transparent z-10 flex flex-row sm:flex-col items-center gap-4 sm:gap-2">
                    <div className="w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600 border border-indigo-100 shrink-0">
                      <Calendar size={20} />
                    </div>
                    <div className="text-left sm:text-center">
                      <p className="text-xs text-gray-400 font-bold uppercase tracking-wide">
                        Check-in
                      </p>
                      <p className="text-lg font-bold text-gray-900">{checkIn}</p>
                    </div>
                  </div>

                  <div className="sm:hidden flex justify-center py-2">
                    <ArrowRight className="text-gray-300 rotate-90" />
                  </div>

                  <div className="flex-1 bg-white sm:bg-transparent z-10 flex flex-row sm:flex-col items-center gap-4 sm:gap-2">
                    <div className="w-12 h-12 rounded-full bg-orange-50 flex items-center justify-center text-orange-600 border border-orange-100 shrink-0">
                      <Calendar size={20} />
                    </div>
                    <div className="text-left sm:text-center">
                      <p className="text-xs text-gray-400 font-bold uppercase tracking-wide">
                        Check-out
                      </p>
                      <p className="text-lg font-bold text-gray-900">{checkOut}</p>
                    </div>
                  </div>
                </div>

                {/* Guests + Status */}
                <div className="bg-gray-50 rounded-xl p-4 sm:p-5 flex flex-col sm:flex-row gap-6 sm:items-center justify-between border border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded-lg shadow-sm text-gray-600">
                      <Users size={20} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Guests</p>
                      <p className="text-sm text-gray-600">{guestsDescription}</p>
                    </div>
                  </div>
                  <div className="h-px w-full sm:h-8 sm:w-px bg-gray-200" />
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white rounded-lg shadow-sm text-gray-600">
                      <ShieldCheck size={20} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Status</p>
                      <span className="inline-flex px-3 py-1 bg-green-100 text-green-800 text-xs font-bold rounded-full border border-green-200">
                        {booking.status?.toUpperCase() || 'CONFIRMED'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer actions */}
              <div className="bg-gray-50/50 p-4 border-t border-gray-100 flex flex-wrap gap-3 justify-between items-center">
                <button
                  onClick={() => {
                    const hostPhone = property.contact?.phone;
                    if (!hostPhone) {
                      alert(`No phone number available for ${property.propertyName}`);
                      return;
                    }

                    const isMobile = window.matchMedia('(max-width: 768px)').matches;
                    if (isMobile) {
                      window.location.href = `tel:${hostPhone}`;
                    } else {
                      setShowHostModal(true);
                    }
                  }}
                  className="flex-1 sm:flex-none py-2.5 px-4 bg-white border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all shadow-sm flex items-center justify-center gap-2"
                >
                  <MessageCircle size={16} /> Contact Host
                </button>
              </div>
            </div>
          </div>

          {/* Right column */}
          <div className="space-y-6">
            {/* Quick actions */}
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={handleAddToCalendar}
                className="p-4 bg-white border border-gray-200 rounded-xl hover:border-indigo-300 hover:shadow-md transition-all group text-left"
              >
                <Calendar className="h-6 w-6 text-indigo-600 mb-3 group-hover:scale-110 transition-transform origin-left" />
                <span className="block text-sm font-bold text-gray-900">Add to Calendar</span>
                <span className="text-xs text-gray-500">Google, Apple, Outlook</span>
              </button>

              <button
                onClick={handleDownloadPDF}
                disabled={isGeneratingPdf || loading}
                className="p-4 bg-white border border-gray-200 rounded-xl hover:border-indigo-300 hover:shadow-md transition-all group text-left disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <Download className="h-6 w-6 text-indigo-600 mb-3 group-hover:scale-110 transition-transform origin-left" />
                <span className="block text-sm font-bold text-gray-900">Download Invoice</span>
                <span className="text-xs text-gray-500">
                  {isGeneratingPdf ? 'Generating...' : 'Receipt & Guide'}
                </span>
              </button>
            </div>

            {/* Payment details */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-5 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                <h3 className="font-bold text-gray-900">Payment Details</h3>
                <span className="px-2.5 py-1 rounded-md text-xs font-bold bg-green-100 text-green-700 border border-green-200">
                  PAID
                </span>
              </div>

              <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                  isDetailsOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="p-5 space-y-3">
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Room Charges</span>
                    <span className="font-medium text-gray-900">
                      ₹
                      {booking.amountPaid?.toLocaleString('en-IN') ||
                        booking.amount?.toLocaleString('en-IN')}
                    </span>
                  </div>
                  <div className="pt-4 border-t border-dashed border-gray-200 flex justify-between items-center">
                    <span className="font-bold text-gray-900">Total</span>
                    <span className="font-bold text-xl text-gray-900">
                      ₹
                      {booking.amountPaid?.toLocaleString('en-IN') ||
                        booking.amount?.toLocaleString('en-IN')}{' '}
                      {booking.currency || 'INR'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 bg-gray-100 p-2 rounded-md font-mono break-all">
                    Payment ID: {booking.razorpayPaymentId || booking.cashfreePaymentId || booking.gatewayOrderId || 'N/A'}
                  </div>
                </div>
              </div>

              <button
                onClick={() => setIsDetailsOpen(!isDetailsOpen)}
                className="w-full py-2 bg-gray-50 hover:bg-gray-100 text-xs text-gray-500 font-medium flex items-center justify-center gap-1 transition-colors border-t border-gray-100"
              >
                {isDetailsOpen ? 'Hide Details' : 'Show Details'}
                {isDetailsOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
            </div>

            {/* Support */}
            <div className="bg-gray-100 rounded-xl p-5 border border-gray-200">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-white rounded-full shadow-sm">
                  <Phone size={16} className="text-gray-600" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900">Need help with this trip?</p>
                  <p className="text-xs text-gray-500 mt-1 mb-3">
                    Our support team is available 24/7.
                  </p>
                  <Link
                    to="/contactus"
                    className="text-sm text-indigo-600 font-semibold hover:text-indigo-800 hover:underline inline-block"
                  >
                    Visit Help Center
                  </Link>
                </div>
              </div>
            </div>

            <div className="text-center pt-2 pb-2">
              <button className="text-sm text-gray-400 hover:text-red-500 transition-colors">
                Cancel Booking
              </button>
            </div>
          </div>
        </div>
      </main>

      {showHostModal && property.contact?.phone && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">Contact Host</h3>
              <button
                onClick={() => setShowHostModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl">
                <div className="p-3 bg-indigo-100 rounded-xl">
                  <Phone size={24} className="text-indigo-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">
                    {property.propertyName || 'Host'}
                  </p>
                  <p className="text-sm text-gray-500 font-mono">
                    {property.contact.phone}
                  </p>
                </div>
              </div>

              <button
                onClick={handleCopyHostNumber}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transition-all shadow-lg"
              >
                {hostCopied ? (
                  <>
                    <Check size={20} className="animate-pulse" />
                    Copied to clipboard!
                  </>
                ) : (
                  <>
                    <Copy size={20} />
                    Copy Number
                  </>
                )}
              </button>

              <button
                onClick={() => (window.location.href = `tel:${property.contact.phone}`)}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-green-600 text-white font-semibold rounded-xl hover:bg-green-700 transition-all shadow-lg"
              >
                <Phone size={20} />
                Call Now
              </button>
            </div>

            <p className="text-xs text-gray-500 text-center mt-4">
              Numbers are verified by property owners
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
